/*
 * PAWFEEDER navigation runtime: announcement bar rotation, product discovery
 * tabs, desktop mega menu keyboard control, language / sort form submission, and
 * the country picker, which is deliberately a front-end affordance (CHG-2026-084).
 */
(function () {
  'use strict';

  function all(selector, root) { return Array.prototype.slice.call((root || document).querySelectorAll(selector)); }

  /* --------------------------------------------- pinned header group metrics */

  /*
   * The announcement bar and the header are separate sibling sections, yet both
   * pin to the top of every page. The bar sticks at 0 and the header has to
   * stick directly beneath it, so the header needs the bar's real rendered
   * height. Measuring it - and re-measuring on resize, orientation change, font
   * load and any box change - keeps the two flush whatever the message wraps
   * to. `--pf-header-height` is published from the same pass so overlays such
   * as the mobile menu panel and the PDP media column can offset themselves
   * under the pinned group.
   *
   * The CSS is gated behind .pf-js on purpose: without JS the bar stays static
   * and scrolls away, which is the safe fallback for a multi-message bar whose
   * height cannot be known up front.
   */
  var docRoot = document.documentElement;
  docRoot.classList.add('pf-js');

  var announcementBar = document.querySelector('[data-pf-announcement]');
  var siteHeader = document.querySelector('[data-pf-header]');

  function syncHeaderMetrics() {
    if (announcementBar) docRoot.style.setProperty('--pf-header-offset', announcementBar.offsetHeight + 'px');
    if (siteHeader) docRoot.style.setProperty('--pf-header-height', siteHeader.offsetHeight + 'px');
  }

  syncHeaderMetrics();
  window.addEventListener('resize', syncHeaderMetrics);
  window.addEventListener('orientationchange', syncHeaderMetrics);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(syncHeaderMetrics);
  if (typeof ResizeObserver === 'function') {
    var headerMetricsObserver = new ResizeObserver(syncHeaderMetrics);
    if (announcementBar) headerMetricsObserver.observe(announcementBar);
    if (siteHeader) headerMetricsObserver.observe(siteHeader);
  }

  /* ----------------------------------------------------- announcement bar */

  /*
   * The messages stack by default so a no-JS visitor reads both. Adding
   * .is-rotating turns the viewport into a fixed-height horizontal track that
   * slides one message width per tick, so the bar height never changes. A
   * clone of the first message is appended so the loop wraps without a visible
   * jump. Rotation pauses on hover, on focus and while the tab is hidden, and
   * is skipped entirely for reduced-motion users.
   */
  all('[data-pf-announcement]').forEach(function (bar) {
    var viewport = bar.querySelector('.pf-announcement__viewport');
    if (!viewport) { return; }
    var items = all('[data-pf-announcement-item]', bar);
    if (items.length < 2) { return; }
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) { return; }

    var interval = parseInt(bar.getAttribute('data-pf-announcement-interval'), 10);
    if (!(interval > 0)) { interval = 5000; }

    var index = 0;
    var timer = null;

    var clone = items[0].cloneNode(true);
    clone.classList.remove('is-active');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('data-pf-announcement-item');
    if (clone.tagName === 'A') { clone.setAttribute('tabindex', '-1'); }
    viewport.appendChild(clone);

    function goTo(next, animate) {
      if (!animate) { viewport.style.transition = 'none'; }
      viewport.style.transform = 'translateX(' + (-next * 100) + '%)';
      if (!animate) {
        void viewport.offsetWidth;
        viewport.style.transition = '';
      }
      index = next;
    }

    viewport.addEventListener('transitionend', function (event) {
      if (event.target !== viewport || event.propertyName !== 'transform') { return; }
      if (index >= items.length) { goTo(0, false); }
    });

    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
    }

    function start() {
      stop();
      timer = setInterval(function () { goTo(index + 1, true); }, interval);
    }

    bar.classList.add('is-rotating');
    start();

    bar.addEventListener('mouseenter', stop);
    bar.addEventListener('mouseleave', start);
    bar.addEventListener('focusin', stop);
    bar.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { stop(); } else { start(); }
    });
  });

  /* --------------------------------------------------------------- tabs */

  all('[data-pf-tabs-root]').forEach(function (root) {
    var tabs = all('[data-pf-tab]', root);
    function select(tab, moveFocus) {
      tabs.forEach(function (item) {
        var selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
        var panel = document.getElementById(item.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
      if (moveFocus) tab.focus();

      /*
       * CHG-2026-070. A tab can be selected while it is still outside the strip:
       * the tabs ask for more width than the strip has at 390px, so the last one
       * starts off-screen, and a selection made where the user cannot see it is
       * not a selection they made. `inline: nearest` moves the strip the smallest
       * amount that reveals the tab, and `block: nearest` keeps the page itself
       * from scrolling to reach it.
       */
      if (typeof tab.scrollIntoView === 'function') {
        tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
      syncEdges(tab.closest('.pf-tabs'));

      /*
       * A rail inside a hidden panel measures 0 x 0, because display:none gives
       * it no layout box. Its arrows are therefore pinned to disabled at boot
       * and nothing ever revisits them: the rail only re-measures on scroll and
       * resize, and neither fires when a tab is clicked. Left alone, every tab
       * but the first keeps two dead arrows even after its rail grows long
       * enough to scroll. Revealing a panel is the one moment that can fix it,
       * so ask the panel we just showed to measure itself.
       */
      var shown = document.getElementById(tab.getAttribute('aria-controls'));
      if (shown) {
        all('[data-pf-rail]', shown).forEach(function (rail) {
          if (typeof rail.pfSync === 'function') rail.pfSync();
        });
      }
    }

    /*
     * CHG-2026-070. Which sides of the strip still hide content. The CSS hangs the
     * fade on this attribute so the fade follows the scroll instead of sitting on
     * an edge permanently: at rest the first tab stays crisp, and at the end of
     * the scroll the last tab does too. A strip that fits keeps no attribute, and
     * therefore no fade at all.
     */
    function syncEdges(strip) {
      if (!strip) return;
      var max = strip.scrollWidth - strip.clientWidth;
      if (max <= 1) { strip.removeAttribute('data-pf-tabs-edges'); return; }
      var atStart = strip.scrollLeft <= 1;
      var atEnd = strip.scrollLeft >= max - 1;
      strip.setAttribute('data-pf-tabs-edges', atStart ? 'end' : atEnd ? 'start' : 'both');
    }

    /* The mask is applied at paint time, so the first state has to be set once the
       strip has its layout box - at this point it does, which is why this runs here
       rather than on DOMContentLoaded. */
    var strip = root.querySelector('.pf-tabs');
    if (strip) {
      strip.addEventListener('scroll', function () { syncEdges(strip); }, { passive: true });
      window.addEventListener('resize', function () { syncEdges(strip); });
      syncEdges(strip);
    }

    tabs.forEach(function (tab, index) {
      tab.tabIndex = index === 0 ? 0 : -1;
      tab.addEventListener('click', function () { select(tab, false); });
      tab.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        event.preventDefault();
        select(tabs[next], true);
      });
    });
  });

  /* -------------------------------------------------- product rail arrows */

  all('[data-pf-rail]').forEach(function (rail) {
    var track = rail.querySelector('[data-pf-rail-track]');
    var prev = rail.querySelector('[data-pf-rail-prev]');
    var next = rail.querySelector('[data-pf-rail-next]');
    if (!track || !prev || !next) return;

    function step() {
      var card = track.firstElementChild;
      return card ? card.getBoundingClientRect().width + 24 : 280;
    }
    function update() {
      var max = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= max - 1;
    }
    prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
    next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();

    // Published so the discovery tabs can re-measure a rail the moment its
    // panel becomes visible - see the comment inside select().
    rail.pfSync = update;
  });

  /* ----------------------------------------------------------- mega menu */

  all('[data-pf-mega-item]').forEach(function (item) {
    var trigger = item.querySelector('[aria-haspopup]');
    var menu = item.querySelector('[data-pf-mega]');
    if (!trigger || !menu) return;

    function alignShopMenu() {
      if (!trigger.hasAttribute('data-pf-shop-trigger')) return;
      var container = menu.offsetParent;
      if (!container) return;
      var left = trigger.getBoundingClientRect().left - container.getBoundingClientRect().left - container.clientLeft;
      menu.style.setProperty('--pf-shop-menu-left', left + 'px');
      menu.style.setProperty('--pf-shop-menu-width', Math.max(0, container.clientWidth - left - 24) + 'px');
    }

    function setOpen(open) {
      if (open) alignShopMenu();
      item.classList.toggle('is-open', open);
      trigger.setAttribute('aria-expanded', String(open));
    }

    trigger.addEventListener('click', function (event) {
      if (trigger.hasAttribute('data-pf-shop-trigger')) {
        event.preventDefault();
        setOpen(true);
        return;
      }
      if (matchMedia('(hover: none)').matches || event.detail === 0) {
        if (!item.classList.contains('is-open')) { event.preventDefault(); setOpen(true); }
      }
    });
    trigger.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowDown') return;
      event.preventDefault();
      setOpen(true);
      var first = menu.querySelector('a');
      if (first) first.focus();
    });
    item.addEventListener('mouseenter', function () { setOpen(true); });
    item.addEventListener('mouseleave', function () { if (!item.contains(document.activeElement)) setOpen(false); });
    item.addEventListener('focusin', function () { setOpen(true); });
    item.addEventListener('focusout', function (event) { if (!item.contains(event.relatedTarget)) setOpen(false); });

    /* Published so the single document-level dismisser below can close this item
       without re-deriving what "closed" means. Same pattern as rail.pfSync. */
    item.pfSetOpen = setOpen;

    if (trigger.hasAttribute('data-pf-shop-trigger')) {
      window.addEventListener('resize', alignShopMenu);
      if (document.fonts) document.fonts.ready.then(alignShopMenu);
    }
  });

  /* A click anywhere outside an open mega closes it. Registered once rather than per
     item, so the loop above cannot install the same listener N times.

     The `item.contains(target)` guard is doing two jobs: it is what makes a click on
     the trigger itself leave the panel open (the trigger's own handler has just opened
     it in the same event, and a naive document handler would close it again
     immediately), and it is what lets a click on a link inside the panel follow that
     link without the menu being torn down underneath the navigation. */
  document.addEventListener('click', function (event) {
    all('[data-pf-mega-item].is-open').forEach(function (item) {
      if (item.contains(event.target)) return;
      if (item.pfSetOpen) item.pfSetOpen(false);
    });
  });

  /* --------------------------------------------------- mobile menu close */

  /*
   * The hamburger panel is a <details>, so the browser opens and closes it with
   * no script at all - which is also why it had no way out: once open, the panel
   * covers the summary that owns the only toggle. CHG-2026-106 puts a close
   * button inside the panel, and this is the whole of its behaviour.
   *
   * Focus moves to the summary before `open` is cleared, not after. Clearing it
   * is what takes the button out of the layout, and focus left on an element that
   * is about to disappear drops the keyboard shopper at the top of the document -
   * the exact failure CHG-2026-104 fixed in the mega menu. Nothing reopens this
   * one on focusin, so the order is not load-bearing here the way it is there;
   * it is kept the same so the two disclosures behave alike.
   */
  all('[data-pf-menu-close]').forEach(function (button) {
    button.addEventListener('click', function () {
      var menu = button.closest('.pf-mobile-menu');
      if (!menu || !menu.open) return;
      var summary = menu.querySelector('summary');
      if (summary) summary.focus();
      menu.open = false;
    });
  });

  /* ------------------------------------------- localization and sorting */

  all('[data-pf-sort]').forEach(function (select) {
    select.addEventListener('change', function () {
      var value = select.value;
      if (value.indexOf('url:') === 0) { location.assign(value.slice(4)); return; }
      if (select.form) select.form.submit();
    });
  });

  all('[data-pf-localization]').forEach(function (select) {
    select.addEventListener('change', function () { if (select.form) select.form.submit(); });
  });

  /*
   * Localization dropdowns. Progressive enhancement: the native selects above stay
   * functional without JS. When JS runs, each [data-pf-locale] swaps to a flag/name
   * trigger with a listbox menu.
   *
   * Two kinds of picker share the widget and they behave differently on purpose. The
   * LANGUAGE picker drives its hidden select and submits, because language changes the
   * copy. The COUNTRY picker only paints - see paintCountry below.
   *
   * They are told apart by the menu itself, not by the hidden select's name: every
   * country row carries a flag glyph and no language row does. The local preview mirror
   * renders those selects without a name attribute, so a name test would silently turn
   * the front-end country picker back into a real submit there.
   */
  var COUNTRY_KEY = 'pf-locale-country';

  function countryRoots() {
    return all('[data-pf-locale]').filter(function (root) {
      var menu = root.querySelector('[data-pf-locale-menu]');
      var first = all('[data-pf-locale-option]', menu)[0];
      return !!(first && first.querySelector('svg'));
    });
  }

  /*
   * Why the country picker is front-end only (CHG-2026-084):
   *
   * Every market this store sells prices in USD and the four markets differ only in
   * name and flag, so switching the real market changes nothing a buyer can see -
   * except the URL. Shopify expresses a market as a path segment (/es is Spanish in the
   * default market, /es-mx is Spanish in Mexico), so a pick only survives while the
   * visitor stays on links carrying that prefix. Landing back on a plain /es - a
   * bookmark, a new tab, an external link - re-derived the country from the URL and put
   * the header back on the United States, which is exactly what made the picker look
   * like it did nothing.
   *
   * So picking a country now paints all three triggers, marks the chosen row and
   * remembers the choice, without touching the URL and without submitting the
   * localization form. The hidden select and its form stay in the markup for the no-JS
   * case, where a real market switch is still the right answer.
   */
  function paintCountry(code) {
    countryRoots().forEach(function (root) {
      var trigger = root.querySelector('[data-pf-locale-trigger]');
      var menu = root.querySelector('[data-pf-locale-menu]');
      if (!trigger || !menu) return;

      var match = null;
      all('[data-pf-locale-option]', menu).forEach(function (option) {
        var on = option.getAttribute('data-pf-locale-option') === code;
        option.classList.toggle('is-selected', on);
        option.setAttribute('aria-selected', String(on));
        if (on) match = option;
      });
      if (!match) return;

      /*
       * The flag travels as markup, not as an asset: copying the chosen row's glyph
       * keeps the trigger's own <svg> (and therefore its sizing class) and swaps only
       * the drawing, so nothing has to be fetched and no new file enters the theme.
       */
      var glyph = match.querySelector('svg');
      var shown = trigger.querySelector('svg');
      if (glyph && shown) shown.innerHTML = glyph.innerHTML;

      var row = match.querySelector('.pf-locale__text') || match;
      var rowText = row.textContent.replace(/\s+/g, ' ').trim();
      var parts = rowText.split(' · ');
      var currencyCode = parts[0];
      var countryName = parts.slice(1).join(' · ');

      /*
       * Two shapes share this trigger (CHG-2026-083): the header prints the currency
       * alone and keeps the country in a visually-hidden span for screen readers, the
       * footer prints the whole "currency . country" pair. The presence of that span is
       * what tells them apart, and updating it is also what keeps the trigger's
       * accessible name truthful after a pick.
       */
      var value = trigger.querySelector('.pf-locale__value');
      var accessible = trigger.querySelector('.visually-hidden');
      if (value) value.textContent = accessible ? currencyCode : rowText;
      if (accessible) accessible.textContent = countryName;

      var select = root.querySelector('[data-pf-localization]');
      if (select) select.value = code;
    });
  }

  function storedCountry() {
    try { return window.localStorage.getItem(COUNTRY_KEY); } catch (error) { return null; }
  }

  function rememberCountry(code) {
    /* Safari in private mode throws on setItem; the pick still applies for this page. */
    try { window.localStorage.setItem(COUNTRY_KEY, code); } catch (error) { /* no storage */ }
  }

  /*
   * Paint the remembered pick before the triggers are revealed below, so the header
   * never flashes the server-rendered country on the way to the remembered one.
   * A market-prefixed URL is an explicit choice - someone followed a link built for
   * that market - so it outranks the stored pick and becomes the new stored value.
   */
  (function restoreCountry() {
    var roots = countryRoots();
    if (!roots.length) return;
    var select = roots[0].querySelector('[data-pf-localization]');
    var serverCode = select ? select.value : null;
    if (!serverCode) return;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '';
    var pinnedToMarket = /^\/[a-z]{2}-[a-z]{2}\//.test(root);
    var code = pinnedToMarket ? serverCode : (storedCountry() || serverCode);
    if (pinnedToMarket || code !== serverCode) paintCountry(code);
    rememberCountry(code);
  })();

  all('[data-pf-locale]').forEach(function (root) {
    var trigger = root.querySelector('[data-pf-locale-trigger]');
    var menu = root.querySelector('[data-pf-locale-menu]');
    var select = root.querySelector('[data-pf-localization]');
    /* The select is optional: the preview mirror renders the rows without one, and a
       picker whose trigger never appears would be worse than one with no fallback. */
    if (!trigger || !menu) return;
    var options = all('[data-pf-locale-option]', menu);
    var isCountry = !!(options[0] && options[0].querySelector('svg'));

    trigger.hidden = false;
    if (select) select.hidden = true;

    /*
     * The header panel is itself a scroll container and this widget sits at its
     * bottom edge, so the in-flow menu it opens (assets/pawfeeder-header.css
     * makes it `position: static` inside the panel) grows straight past the
     * panel's visible bottom. Measured on the live page at 390x844 with the
     * menu open, the panel's visible edge is at y=831 while the language rows
     * run to y=862 - only 23 of the 41 pixels of the "Espanol" row are on
     * screen, and a tap there lands on the page section behind. Opening a menu
     * therefore has to bring it into view, the way the browser does for a
     * focused element.
     *
     * Only the nearest real scroll container is adjusted, and only when the menu
     * actually overflows it: the footer's popover opens upward as an absolutely
     * positioned card and has nothing to scroll, so this is a no-op there.
     */
    function revealMenu() {
      var box = menu.getBoundingClientRect();
      var host = menu.parentElement;
      while (host && host !== document.body && host !== document.documentElement) {
        var styles = getComputedStyle(host);
        if (/(auto|scroll)/.test(styles.overflowY) && host.scrollHeight > host.clientHeight + 1) {
          var limiter = Math.min(host.getBoundingClientRect().bottom, window.innerHeight);
          var overflow = box.bottom - limiter;
          if (overflow > 0) host.scrollTop += overflow + 12;
          return;
        }
        host = host.parentElement;
      }
    }

    function setOpen(open, moveFocus) {
      menu.hidden = !open;
      trigger.setAttribute('aria-expanded', String(open));
      if (open && moveFocus) {
        var current = menu.querySelector('.is-selected') || options[0];
        if (current) current.focus();
      }
      if (open) revealMenu();
    }

    trigger.addEventListener('click', function () { setOpen(menu.hidden, true); });
    trigger.addEventListener('keydown', function (event) {
      if ((event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') && menu.hidden) {
        event.preventDefault();
        setOpen(true, true);
      }
    });

    options.forEach(function (option, index) {
      option.addEventListener('click', function () {
        var code = option.getAttribute('data-pf-locale-option');
        if (isCountry) {
          /* Front-end only: no select change, no form submit, no URL change. The menu
             closes and focus returns to the trigger, which now reads the new country -
             the same landing place the submit used to leave the visitor at. */
          paintCountry(code);
          rememberCountry(code);
          setOpen(false, false);
          trigger.focus();
          return;
        }
        if (!select) return;
        select.value = code;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
      option.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowDown') next = options[index + 1];
        if (event.key === 'ArrowUp') next = options[index - 1];
        if (event.key === 'Home') next = options[0];
        if (event.key === 'End') next = options[options.length - 1];
        if (event.key === 'Escape') { setOpen(false, false); trigger.focus(); return; }
        if (!next) return;
        event.preventDefault();
        next.focus();
      });
    });

    document.addEventListener('click', function (event) {
      if (!root.contains(event.target)) setOpen(false, false);
    });
    /*
     * `relatedTarget` is null whenever focus is not moving to another element,
     * and `root.contains(null)` is false - so the old form of this handler
     * closed the menu on every such focusout. That is the whole bug on Safari:
     * opening the menu focuses the current row, Safari does not focus a
     * <button> when it is tapped, so tapping a different row fires focusout
     * with relatedTarget === null BEFORE the click is dispatched. The menu was
     * removed from under the finger, WebKit then found the mousedown and
     * mouseup targets to be different elements and dispatched the click on
     * their common ancestor (measured: click reached .pf-footer__signup, never
     * the option), so the option's handler never set the select and nothing
     * happened. Chromium focuses the tapped button, which is why it only ever
     * failed on Safari. A click outside still closes the menu through the
     * document listener below.
     */
    root.addEventListener('focusout', function (event) {
      if (event.relatedTarget === null) return;
      if (!root.contains(event.relatedTarget)) setOpen(false, false);
    });
    root.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !menu.hidden) { setOpen(false, false); trigger.focus(); }
    });
  });

  /* --------------------------------------------------- search suggestion */

  /*
   * Hosted by the search page's own field since CHG-2026-042. The header magnifier
   * is a plain link to /search, so this pair is rendered by
   * sections/pawfeeder-search.liquid behind the search_predictive setting, and the
   * popover controller that used to open it from the header is gone with it. The
   * lookup is by attribute, not by id, so any other field that opts into the same
   * two hooks gets the behaviour for free.
   */

  var searchField = document.querySelector('[data-pf-search-field]');
  var searchPanel = document.querySelector('[data-pf-search-panel]');
  if (searchField && searchPanel) {
    var timer = null;
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

    function hide() { searchPanel.hidden = true; searchPanel.innerHTML = ''; searchField.setAttribute('aria-expanded', 'false'); }

    function render(results) {
      if (!results.length) { hide(); return; }
      searchPanel.innerHTML = results.map(function (item) {
        return '<a role="option" href="' + escapeHtml(item.url) + '"><img src="' + escapeHtml(item.image || '') + '" alt="" width="48" height="48" loading="lazy"><span><b>' + item.title + '</b><small>' + (item.sku ? (I18N.item_label || 'Item') + ' ' + item.sku : '') + '</small></span></a>';
      }).join('');
      searchPanel.hidden = false;
      searchField.setAttribute('aria-expanded', 'true');
    }

    function escapeHtml(value) {
      var node = document.createElement('span');
      node.textContent = value == null ? '' : String(value);
      return node.innerHTML;
    }

    searchField.addEventListener('input', function () {
      var term = searchField.value.trim();
      clearTimeout(timer);
      if (term.length < 2) { hide(); return; }
      timer = setTimeout(function () {
        fetch(root + 'search/suggest.json?q=' + encodeURIComponent(term) + '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last')
          .then(function (response) { return response.ok ? response.json() : { resources: { results: { products: [] } } }; })
          .then(function (payload) {
            var products = (payload.resources && payload.resources.results && payload.resources.results.products) || [];
            render(products.map(function (product) {
              return {
                url: product.url,
                title: escapeHtml(product.title),
                sku: product.variants && product.variants[0] ? escapeHtml(product.variants[0].sku || '') : '',
                image: product.featured_image && product.featured_image.url ? product.featured_image.url : ''
              };
            }));
          })
          .catch(hide);
      }, 220);
    });

    searchField.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') { hide(); return; }
      if (event.key !== 'ArrowDown' || searchPanel.hidden) return;
      var first = searchPanel.querySelector('a');
      if (first) { event.preventDefault(); first.focus(); }
    });

    searchPanel.addEventListener('keydown', function (event) {
      var links = all('a', searchPanel);
      var index = links.indexOf(document.activeElement);
      if (event.key === 'Escape') { hide(); searchField.focus(); return; }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      var next = event.key === 'ArrowDown' ? Math.min(links.length - 1, index + 1) : Math.max(0, index - 1);
      if (links[next]) links[next].focus();
    });

    document.addEventListener('click', function (event) {
      if (!searchPanel.contains(event.target) && event.target !== searchField) hide();
    });
  }
})();
